// crewbit-admin/src/components/money/settings/HstNumberEditor.tsx
//
// HST number input with CRA pattern validation `^\d{9}RT\d{4}$` (mirrors
// server-side regex per Decision §Settings.D2). Inline-save with 1.5s
// debounce + SaveConfirmationPill flash per Decision §Settings.D3.
// Invalid format gates the save (debounce schedules but onSave validation
// rejects; UI flags inline).
//
// Mirrors crewbit-mobile/components/money/settings/HstNumberEditor.tsx with
// web idiom (HTMLInput in place of TextInput, inline styles).

import { useEffect, useRef, useState } from 'react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import SaveConfirmationPill from './SaveConfirmationPill';

const DEBOUNCE_MS = 1500;
const PILL_VISIBLE_MS = 2000;
const HST_REGEX = /^[0-9]{9}RT[0-9]{4}$/;

export type HstSaveResult =
  | { success: true }
  | { success: false; message?: string };

interface HstNumberEditorProps {
  label: string;
  description?: string;
  placeholder?: string;
  invalidFormatLabel: string;
  value: string | null;
  onSave: (next: string | null) => Promise<HstSaveResult>;
}

function isValid(input: string): boolean {
  if (input === '') return true; // empty clears stored value
  return HST_REGEX.test(input);
}

export default function HstNumberEditor({
  label,
  description,
  placeholder = '123456789RT0001',
  invalidFormatLabel,
  value,
  onSave,
}: HstNumberEditorProps) {
  const [localValue, setLocalValue] = useState<string>(value ?? '');
  const [touched, setTouched] = useState(false);
  const [pillVisible, setPillVisible] = useState(false);
  const [pillKind, setPillKind] = useState<'success' | 'warning'>('success');
  const [pillLabel, setPillLabel] = useState<string | undefined>(undefined);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync of upstream value into local draft after refetch
    setLocalValue((prev) => (prev === (value ?? '') ? prev : value ?? ''));
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    };
  }, []);

  const flashPill = (kind: 'success' | 'warning', flashLabel?: string) => {
    if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    setPillKind(kind);
    setPillLabel(flashLabel);
    setPillVisible(true);
    pillTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setPillVisible(false);
    }, PILL_VISIBLE_MS);
  };

  const handleChange = (next: string) => {
    setTouched(true);
    setLocalValue(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isValid(next)) return;
    debounceRef.current = setTimeout(async () => {
      const result = await onSave(next === '' ? null : next);
      if (!mountedRef.current) return;
      if (result.success) flashPill('success');
      else flashPill('warning', result.message);
    }, DEBOUNCE_MS);
  };

  const showInlineError = touched && !isValid(localValue);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, padding: spacing.md }}>
      <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
        {label}
      </div>
      {description && (
        <div style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
      <input
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value.toUpperCase())}
        placeholder={placeholder}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        style={{
          fontSize: typography.size.body,
          padding: spacing.sm,
          borderRadius: radii.sm,
          border: `1px solid ${showInlineError ? palette.amber : palette.g200}`,
          background: palette.white,
          color: palette.g800,
          fontFamily: 'inherit',
          fontVariantNumeric: 'tabular-nums',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
      {showInlineError && (
        <div style={{ fontSize: typography.size.caption, color: palette.amber, fontWeight: 600 }}>
          {invalidFormatLabel}
        </div>
      )}
      <div style={{ minHeight: 18, marginTop: spacing.xxs }}>
        <SaveConfirmationPill visible={pillVisible} kind={pillKind} label={pillLabel} />
      </div>
    </div>
  );
}
